import React from 'react';
import { useTranslation } from 'react-i18next';
import useInstallPrompt from '../../hooks/useInstallPrompt';
import './InstallPrompt.css';

/**
 * Erbjuder användaren att installera appen på hemskärmen.
 *
 * Fanns inte tidigare, vilket gjorde att i praktiken ingen installerade
 * appen — och allt som kräver en installerad app blev därmed oåtkomligt:
 * notiser (på iOS är web push omöjligt utanför hemskärmsläget), knapparna i
 * kö-notisen och genvägarna på ikonen.
 *
 * Två olika texter, för att plattformarna skiljer sig i grunden: Android får
 * en knapp som öppnar webbläsarens installationsdialog, iOS får instruktionen
 * eftersom Safari inte har någon sådan dialog att öppna. Visas aldrig när
 * appen redan körs installerad, och ett "inte nu" håller tyst i 30 dagar.
 *
 * @returns {JSX.Element|null} Bannern, eller null när det inte finns något
 *   att erbjuda.
 */
export default function InstallPrompt() {
  const { t } = useTranslation();
  const {
    canInstall, needsManualInstall, dismissed, promptInstall, dismiss,
  } = useInstallPrompt();

  if (dismissed) return null;
  if (!canInstall && !needsManualInstall) return null;

  return (
    <div className="install-prompt" role="dialog" aria-label={t('pwa.installTitle')}>
      <span className="install-prompt-icon" aria-hidden="true">📲</span>
      <div className="install-prompt-body">
        <strong className="install-prompt-title">{t('pwa.installTitle')}</strong>
        {canInstall ? (
          <>
            <span className="install-prompt-text">{t('pwa.installText')}</span>
            <div className="install-prompt-actions">
              <button
                type="button"
                className="install-prompt-install"
                onClick={promptInstall}
              >
                {t('pwa.installButton')}
              </button>
              <button type="button" className="install-prompt-later" onClick={dismiss}>
                {t('pwa.installLater')}
              </button>
            </div>
          </>
        ) : (
          <>
            <span className="install-prompt-text">{t('pwa.installTextIos')}</span>
            <ol className="install-prompt-steps">
              <li>{t('pwa.installIosStep1')}</li>
              <li>{t('pwa.installIosStep2')}</li>
            </ol>
          </>
        )}
      </div>
      <button
        type="button"
        className="install-prompt-close"
        onClick={dismiss}
        aria-label={t('pwa.installDismiss')}
        title={t('pwa.installDismiss')}
      >
        ✕
      </button>
    </div>
  );
}
